import React, { useState, useEffect, useRef } from 'react';
import TimelineIcon, { getAvailableIcons, getAvailablePacks } from './TimelineIcons';
import { useLanguage } from '../contexts/LanguageContext';

const PRESET_COLORS = [
  { name: 'Default', value: '' },
  { name: 'Charcoal', value: '#222222' },
  { name: 'Sleek Gray', value: '#666666' },
  { name: 'Elegant Gold', value: '#d4af37' },
  { name: 'Rose Gold', value: '#c88e8d' },
  { name: 'Sage Green', value: '#8a9a86' },
  { name: 'Navy Blue', value: '#1a2e40' },
  { name: 'Terracotta', value: '#c06c5c' },
  { name: 'Soft Cream', value: '#fdfbf7' },
  { name: 'Pure White', value: '#ffffff' }
];

const THEME_COLORS = [
  { name: 'Primary', value: 'var(--color-primary)', hex: '#6b8e6f' },
  { name: 'Accent', value: 'var(--color-accent)', hex: '#d4a574' },
  { name: 'Text', value: 'var(--color-text)', hex: '#3d3d3d' },
  { name: 'Text Light', value: 'var(--color-text-light)', hex: '#8a8a8a' }
];

export function GenericButton({ item, isEditMode, onClick }) {
  const { l } = useLanguage();
  const buttonStyle = {
    '--btn-bg': item.color || undefined,
    '--btn-text': item.textColor || undefined,
  };

  const currentPack = item.pack || 'default';

  return (
    <div className={`generic-button-container align-${item.align || 'center'}`} style={{ margin: isEditMode ? '0.25rem 0' : undefined }}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        href={isEditMode ? '#' : (item.url && item.url.startsWith('/') ? l(item.url) : (item.url || '#'))}
        onClick={onClick}
        className={`generic-btn generic-btn-${item.styleType || 'solid'}`}
        style={buttonStyle}
        target={(!isEditMode && item.openInNewTab) ? "_blank" : undefined}
        rel={(!isEditMode && item.openInNewTab) ? "noopener noreferrer" : undefined}
      >
        {item.icon && (
          <TimelineIcon name={item.icon} pack={currentPack} />
        )}
        <span>{item.text || 'Button'}</span>
      </a>
    </div>
  );
}

export function GenericButtonEditor({ item, onChange, onClose }) {
  const [customColor, setCustomColor] = useState(item.color && !item.color.startsWith('var') ? item.color : '#6b8e6f');
  const [customTextColor, setCustomTextColor] = useState(item.textColor && !item.textColor.startsWith('var') ? item.textColor : '#ffffff');
  const [uploadedIcons, setUploadedIcons] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const checkboxId = useRef(`open-tab-${Math.random().toString(36).substring(2, 9)}`).current;

  // Fetch custom uploaded icons on load
  useEffect(() => {
    fetch('/api/custom-icons')
      .then(res => res.json())
      .then(data => {
        if (data.icons) {
          setUploadedIcons(data.icons);
        }
      })
      .catch(err => console.error('Failed to load custom icons:', err));
  }, []);

  const handleFieldChange = (field, value) => {
    onChange({ ...item, [field]: value });
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedExtensions = ['.svg', '.png', '.jpg', '.jpeg', '.webp'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      setUploadError('Only SVG, PNG, JPG, and WEBP files can be uploaded as custom icons.');
      return;
    }

    setUploadError('');
    const formData = new FormData();
    formData.append('photo', file);

    const token = localStorage.getItem('editToken'); // get jwt token for edit authorization

    try {
      const response = await fetch('/api/upload-icon', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success && data.icon) {
        setUploadedIcons(prev => [...prev, data.icon]);
        // Update the button settings to use this new uploaded icon
        onChange({
          ...item,
          pack: 'uploaded',
          icon: data.icon.url
        });
      } else {
        setUploadError(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Failed to upload icon:', error);
      setUploadError('Network error uploading icon');
    }
  };

  const availablePacks = [...getAvailablePacks(), 'uploaded'];
  const currentPack = item.pack || 'default';
  
  // If pack is uploaded, icons are list of custom urls, otherwise lookup from default packs
  const availableIcons = currentPack === 'uploaded' 
    ? uploadedIcons.map(ic => ({ name: ic.name, value: ic.url })) 
    : getAvailableIcons(currentPack).map(ic => ({ name: ic, value: ic }));

  const colors = [...THEME_COLORS, ...PRESET_COLORS.filter(c => c.value)];

  return (
    <div className="button-editor-panel" style={{ width: '100%', maxWidth: '800px', textAlign: 'left', zIndex: 10 }}>
      <h4 style={{ marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', color: 'var(--color-primary)' }}>
        Configure Button
      </h4>
      <div className="button-editor-grid">
        {/* Column 1: Text & Link */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="button-editor-field">
            <label>Button Text</label>
            <input
              type="text"
              value={item.text || ''}
              onChange={e => handleFieldChange('text', e.target.value)}
              className="button-editor-input"
              placeholder="e.g. RSVP Here"
            />
          </div>
          <div className="button-editor-field">
            <label>Hyperlink URL</label>
            <input
              type="text"
              value={item.url || ''}
              onChange={e => handleFieldChange('url', e.target.value)}
              className="button-editor-input"
              placeholder="e.g. #rsvp or https://google.com"
            />
          </div>
          <div className="button-editor-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', marginTop: '-0.2rem', marginBottom: '0.2rem' }}>
            <input
              type="checkbox"
              id={checkboxId}
              checked={!!item.openInNewTab}
              onChange={e => handleFieldChange('openInNewTab', e.target.checked)}
              style={{ width: 'auto', margin: 0, cursor: 'pointer' }}
            />
            <label htmlFor={checkboxId} style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.85rem', fontWeight: 600, color: '#555', margin: 0 }}>
              Open in new tab
            </label>
          </div>
          <div className="button-editor-field">
            <label>Alignment</label>
            <div className="button-align-options">
              {['left', 'center', 'right'].map(alignVal => (
                <button
                  key={alignVal}
                  type="button"
                  className={`button-align-btn ${item.align === alignVal ? 'active' : ''}`}
                  onClick={() => handleFieldChange('align', alignVal)}
                >
                  {alignVal.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Column 2: Style & Icon */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="button-editor-field">
            <label>Button Style</label>
            <select
              value={item.styleType || 'solid'}
              onChange={e => handleFieldChange('styleType', e.target.value)}
              className="button-editor-select"
            >
              <option value="solid">Solid Background</option>
              <option value="outline">Outline Border</option>
              <option value="glassmorphic">Glassmorphic (blur)</option>
            </select>
          </div>
          <div className="button-editor-field">
            <label>Icon Pack</label>
            <select
              value={currentPack}
              onChange={e => {
                const newPack = e.target.value;
                if (newPack === 'uploaded') {
                  onChange({
                    ...item,
                    pack: newPack,
                    icon: uploadedIcons[0]?.url || ''
                  });
                } else {
                  const icons = getAvailableIcons(newPack);
                  onChange({
                    ...item,
                    pack: newPack,
                    icon: icons[0] || ''
                  });
                }
              }}
              className="button-editor-select"
            >
              {availablePacks.map(pack => (
                <option key={pack} value={pack}>{pack}</option>
              ))}
            </select>
          </div>
          <div className="button-editor-field">
            <label>Icon (Optional)</label>
            <select
              value={item.icon || ''}
              onChange={e => handleFieldChange('icon', e.target.value)}
              className="button-editor-select"
            >
              <option value="">No Icon</option>
              {availableIcons.map(ic => (
                <option key={ic.value} value={ic.value}>{ic.name}</option>
              ))}
            </select>
          </div>
          
          {/* Upload Custom Icon form */}
          <div className="button-editor-field" style={{ marginTop: '0.2rem' }}>
            <label style={{ fontSize: '0.75rem' }}>Upload Custom Icon (.svg, .png, .jpg, .webp)</label>
            <input
              type="file"
              accept=".svg,.png,.jpg,.jpeg,.webp"
              onChange={handleIconUpload}
              style={{ fontSize: '0.8rem' }}
            />
            {uploadError && (
              <span style={{ color: '#d9534f', fontSize: '0.75rem', marginTop: '0.2rem' }}>{uploadError}</span>
            )}
          </div>
        </div>

        {/* Column 3: Colors */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="button-editor-field">
            <label>Button Color</label>
            <div className="button-color-swatches">
              {colors.map(col => (
                <button
                  key={col.name}
                  type="button"
                  className={`button-color-swatch ${(item.color || '') === col.value ? 'active' : ''}`}
                  style={{ background: col.hex || col.value || '#ccc' }}
                  title={col.name}
                  onClick={() => handleFieldChange('color', col.value)}
                />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#666' }}>Custom:</span>
              <input
                type="color"
                value={customColor}
                onChange={e => {
                  setCustomColor(e.target.value);
                  handleFieldChange('color', e.target.value);
                }}
                style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}
              />
              <input
                type="text"
                value={item.color || ''}
                onChange={e => handleFieldChange('color', e.target.value)}
                placeholder="#hex code"
                style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', width: '80px' }}
              />
            </div>
          </div>

          <div className="button-editor-field">
            <label>Text Color</label>
            <div className="button-color-swatches">
              {[
                { name: 'White', value: '#ffffff' },
                { name: 'Dark Gray', value: '#3d3d3d' },
                { name: 'Gold', value: '#d4af37' },
                { name: 'Accent', value: 'var(--color-accent)' },
                { name: 'Primary', value: 'var(--color-primary)' }
              ].map(col => (
                <button
                  key={col.name}
                  type="button"
                  className={`button-color-swatch ${(item.textColor || '') === col.value ? 'active' : ''}`}
                  style={{ background: col.value }}
                  title={col.name}
                  onClick={() => handleFieldChange('textColor', col.value)}
                />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#666' }}>Custom:</span>
              <input
                type="color"
                value={customTextColor}
                onChange={e => {
                  setCustomTextColor(e.target.value);
                  handleFieldChange('textColor', e.target.value);
                }}
                style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}
              />
              <input
                type="text"
                value={item.textColor || ''}
                onChange={e => handleFieldChange('textColor', e.target.value)}
                placeholder="#hex code"
                style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', width: '80px' }}
              />
            </div>
          </div>
        </div>
      </div>
      {onClose && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={onClose}
            className="btn-small"
            style={{ padding: '0.5rem 1rem' }}
          >
            Done Editing
          </button>
        </div>
      )}
    </div>
  );
}

export default GenericButton;
